import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Trash2, Edit2, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MyActivityPage() {
  const { user } = useAuth();
  
  // State
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Date selection
  const [selectedDate, setSelectedDate] = useState(getTodayIST());
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: get7DaysAgoIST(),
    to: getTodayIST()
  });
  
  // Form state
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: getTodayIST(),
    start_time: '',
    end_time: '',
    activity_type: '',
    firm: '',
    notes: ''
  });
  
  // Follow-up conversion states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [convertingEntry, setConvertingEntry] = useState(null);
  const [followUpFirmSearch, setFollowUpFirmSearch] = useState('');
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpOutcome, setFollowUpOutcome] = useState('interested');
  const [followUpNextDate, setFollowUpNextDate] = useState('');
  const [activeFirms, setActiveFirms] = useState([]);
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [followUpError, setFollowUpError] = useState('');
  const [followUpSuccess, setFollowUpSuccess] = useState(false);
  
  // Collapsible day view for range mode
  const [expandedDays, setExpandedDays] = useState({});
  
  // Helper functions
  function getTodayIST() {
    const now = new Date();
    const offset = 5.5 * 60; // IST offset
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const istDate = new Date(utc + (offset * 60000));
    return istDate.toISOString().split('T')[0];
  }
  
  function get7DaysAgoIST() {
    const now = new Date();
    now.setDate(now.getDate() - 7);
    const offset = 5.5 * 60;
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const istDate = new Date(utc + (offset * 60000));
    return istDate.toISOString().split('T')[0];
  }
  
  function formatDuration(hours) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/timesheet/categories`, {
          withCredentials: true
        });
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  // Fetch active trial firms (for follow-up conversion)
  useEffect(() => {
    const fetchActiveFirms = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/firms`, {
          withCredentials: true
        });
        const active = data.filter(f => f.status === 'active');
        setActiveFirms(active);
      } catch (error) {
        console.error('Failed to fetch active firms:', error);
      }
    };
    fetchActiveFirms();
  }, []);
  
  // Fetch entries
  useEffect(() => {
    fetchEntries();
  }, [selectedDate, isRangeMode, dateRange]);
  
  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = isRangeMode
        ? { from_date: dateRange.from, to_date: dateRange.to }
        : { from_date: selectedDate, to_date: selectedDate };
      
      const { data } = await axios.get(`${API_URL}/api/my-activity`, {
        params,
        withCredentials: true
      });
      
      setEntries(data.entries || []);
      
      // Initialize expanded state for range mode - collapse all initially
      if (isRangeMode) {
        const initialExpanded = {};
        const uniqueDates = [...new Set((data.entries || []).map(e => e.date))];
        uniqueDates.forEach(date => {
          initialExpanded[date] = false; // Collapsed by default
        });
        setExpandedDays(initialExpanded);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleDayExpansion = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };
  
  // Group entries by date for range mode
  const getEntriesByDate = () => {
    const grouped = {};
    entries.forEach(entry => {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      grouped[entry.date].push(entry);
    });
    return grouped;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEntry) {
        // Update
        await axios.patch(
          `${API_URL}/api/my-activity/${editingEntry}`,
          formData,
          { withCredentials: true }
        );
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/my-activity`,
          formData,
          { withCredentials: true }
        );
      }
      
      setShowEntryForm(false);
      setEditingEntry(null);
      setFormData({
        date: selectedDate,
        start_time: '',
        end_time: '',
        activity_type: '',
        firm: '',
        notes: ''
      });
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save entry');
    }
  };
  
  const handleEdit = (entry) => {
    setEditingEntry(entry.entry_id);
    setFormData({
      date: entry.date || '',
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      activity_type: entry.activity_type || '',
      firm: entry.firm || '',
      notes: entry.notes || ''
    });
    setShowEntryForm(true);
  };
  
  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/my-activity/${entryId}`, {
        withCredentials: true
      });
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete entry');
    }
  };
  
  // Handle convert to follow-up
  const handleConvertToFollowUp = (entry) => {
    setConvertingEntry(entry);
    setFollowUpNote(entry.notes || '');
    setFollowUpFirmSearch('');
    setSelectedFirm(null);
    setFollowUpOutcome('interested');
    setFollowUpNextDate('');
    setFollowUpError('');
    setShowFollowUpModal(true);
  };
  
  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFirm) {
      setFollowUpError('Please select a trial firm');
      return;
    }
    
    if (followUpNote.trim().length < 10) {
      setFollowUpError('Follow-up note must be at least 10 characters long');
      return;
    }
    
    setSavingFollowUp(true);
    setFollowUpError('');
    
    try {
      await axios.post(
        `${API_URL}/api/firms/${encodeURIComponent(selectedFirm.site_url)}/follow-ups`,
        {
          note: followUpNote,
          outcome: followUpOutcome,
          next_follow_up_date: followUpNextDate || null
        },
        { withCredentials: true }
      );
      
      setShowFollowUpModal(false);
      setFollowUpSuccess(true);
      setTimeout(() => setFollowUpSuccess(false), 3000);
      setConvertingEntry(null);
    } catch (error) {
      setFollowUpError(error.response?.data?.detail || 'Error creating follow-up. Please try again.');
    } finally {
      setSavingFollowUp(false);
    }
  };
  
  const handleFollowUpCancel = () => {
    setShowFollowUpModal(false);
    setConvertingEntry(null);
    setFollowUpError('');
  };
  
  // Filter firms for search
  const filteredFirms = followUpFirmSearch
    ? activeFirms.filter(f =>
        f.firm_name.toLowerCase().includes(followUpFirmSearch.toLowerCase()) ||
        f.site_url.toLowerCase().includes(followUpFirmSearch.toLowerCase())
      )
    : [];
  
  const totalHours = entries.reduce((sum, e) => sum + e.duration_hours, 0);
  const totalHoursFormatted = formatDuration(totalHours);
  
  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#09090B]">My Activity</h1>
          <p className="text-sm text-[#52525B] mt-1">Track your personal activities</p>
        </div>
        
        {/* Date Selection */}
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="flex items-end justify-between gap-4">
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-xs text-[#71717A] mb-1">From Date</label>
                <input
                  type="date"
                  value={isRangeMode ? dateRange.from : selectedDate}
                  max={getTodayIST()}
                  onChange={(e) => {
                    if (isRangeMode) {
                      setDateRange({ ...dateRange, from: e.target.value });
                    } else {
                      setSelectedDate(e.target.value);
                    }
                  }}
                  className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                />
              </div>

              {isRangeMode && (
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">To Date</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    max={getTodayIST()}
                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  />
                </div>
              )}

              {/* Range Mode Toggle */}
              <div className="flex gap-1 border border-[#E4E4E7]">
                <button
                  onClick={() => setIsRangeMode(false)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    !isRangeMode
                      ? 'bg-[#0C9168] text-white'
                      : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                  }`}
                >
                  Single
                </button>
                <button
                  onClick={() => setIsRangeMode(true)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    isRangeMode
                      ? 'bg-[#0C9168] text-white'
                      : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                  }`}
                >
                  Range
                </button>
              </div>
            </div>
            
            <button
              onClick={() => {
                setEditingEntry(null);
                setFormData({
                  date: selectedDate,
                  start_time: '',
                  end_time: '',
                  activity_type: '',
                  firm: '',
                  notes: ''
                });
                setShowEntryForm(true);
              }}
              className="bg-[#0C9168] text-white hover:bg-[#097352] px-4 py-2 font-medium transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Add Entry
            </button>
          </div>
        </div>
        
        {/* Entry Form Modal */}
        {showEntryForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-[#09090B] mb-4">
                {editingEntry ? 'Edit Entry' : 'Add Entry'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">Start Time *</label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">End Time *</label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-1">Activity Type *</label>
                  <select
                    value={formData.activity_type}
                    onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  >
                    <option value="">Select activity</option>
                    <optgroup label="Revenue Generating Activities (RGA)">
                      {categories.filter(c => c.type === 'RGA').map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Non-Revenue Activities (NRA)">
                      {categories.filter(c => c.type === 'NRA').map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-1">Client / Lead Name</label>
                  <input
                    type="text"
                    value={formData.firm}
                    onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    placeholder="Enter client or lead name (optional)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    placeholder="Add notes (optional)"
                  />
                </div>
                
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEntryForm(false);
                      setEditingEntry(null);
                    }}
                    className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#0C9168] text-white hover:bg-[#097352] transition-colors"
                  >
                    {editingEntry ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Entries Table */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A]">
              Activity Entries
            </h2>
            <div className="text-sm text-[#52525B]">
              <span>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
              {entries.length > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <span className="font-bold text-[#09090B]">{totalHoursFormatted} total</span>
                </>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-[#52525B]">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-[#52525B]">
              No entries found for the selected date{isRangeMode ? ' range' : ''}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                  <tr>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">
                      {isRangeMode ? 'Date/Time' : 'Time'}
                    </th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Duration</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Activity</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Type</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Client/Lead</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Notes</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isRangeMode ? (
                    // Range mode: Group by date with collapsible sections
                    Object.keys(getEntriesByDate()).sort().reverse().map(date => {
                      const dayEntries = getEntriesByDate()[date];
                      const isExpanded = expandedDays[date];
                      const dayTotalHours = dayEntries.reduce((sum, e) => sum + e.duration_hours, 0);
                      const rgaTotalHours = dayEntries.filter(e => e.rga_nra === 'RGA').reduce((sum, e) => sum + e.duration_hours, 0);
                      const nraTotalHours = dayEntries.filter(e => e.rga_nra === 'NRA').reduce((sum, e) => sum + e.duration_hours, 0);
                      
                      return (
                        <React.Fragment key={date}>
                          {/* Date Section Header Row */}
                          <tr className="bg-[#FAFAFA] hover:bg-[#F4F4F5] border-t-2 border-[#E4E4E7]">
                            <td colSpan="7" className="p-0">
                              <button
                                onClick={() => toggleDayExpansion(date)}
                                className="w-full p-4 flex items-center gap-3 text-left"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-[#52525B]" strokeWidth={2} />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-[#52525B]" strokeWidth={2} />
                                )}
                                <span className="text-sm font-bold text-[#09090B]">
                                  {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                <span className="text-xs text-[#71717A]">
                                  {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                                </span>
                                <span className="text-sm font-bold text-[#09090B]">
                                  {formatDuration(dayTotalHours)} total
                                </span>
                                {(rgaTotalHours > 0 || nraTotalHours > 0) && (
                                  <>
                                    <span className="text-xs text-[#71717A]">•</span>
                                    {rgaTotalHours > 0 && (
                                      <span className="text-xs text-green-700 font-medium">
                                        RGA: {formatDuration(rgaTotalHours)}
                                      </span>
                                    )}
                                    {nraTotalHours > 0 && (
                                      <span className="text-xs text-gray-700 font-medium">
                                        NRA: {formatDuration(nraTotalHours)}
                                      </span>
                                    )}
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                          
                          {/* Entry Rows for this Date */}
                          {isExpanded && dayEntries.map((entry, idx) => (
                            <tr key={entry.entry_id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                              <td className="p-3 text-sm text-[#52525B] font-mono">{entry.time}</td>
                              <td className="p-3 text-sm text-[#09090B] font-medium">{formatDuration(entry.duration_hours)}</td>
                              <td className="p-3 text-sm text-[#52525B]">{entry.activity_type || '—'}</td>
                              <td className="p-3">
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                                  entry.rga_nra === 'RGA' 
                                    ? 'bg-green-100 text-green-900 border border-green-900'
                                    : entry.rga_nra === 'NRA'
                                    ? 'bg-gray-100 text-gray-900 border border-gray-900'
                                    : ''
                                }`}>
                                  {entry.rga_nra || '—'}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-[#52525B]">{entry.firm || '—'}</td>
                              <td className="p-3 text-sm text-[#52525B]">{entry.notes || '—'}</td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEdit(entry)}
                                    className="text-[#0C9168] hover:text-[#097352]"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" strokeWidth={2} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(entry.entry_id)}
                                    className="text-[#DC2626] hover:text-[#B91C1C]"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" strokeWidth={2} />
                                  </button>
                                  <button
                                    onClick={() => handleConvertToFollowUp(entry)}
                                    className="text-[#0C9168] hover:text-[#097352]"
                                    title="Convert to Follow-up"
                                  >
                                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    // Single date mode: Simple flat list
                    entries.map((entry, idx) => (
                      <tr key={entry.entry_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}>
                        <td className="p-3 text-sm text-[#52525B] font-mono">{entry.time}</td>
                        <td className="p-3 text-sm text-[#09090B] font-medium">{formatDuration(entry.duration_hours)}</td>
                        <td className="p-3 text-sm text-[#52525B]">{entry.activity_type || '—'}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                            entry.rga_nra === 'RGA' 
                              ? 'bg-green-100 text-green-900 border border-green-900'
                              : entry.rga_nra === 'NRA'
                              ? 'bg-gray-100 text-gray-900 border border-gray-900'
                              : ''
                          }`}>
                            {entry.rga_nra || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-[#52525B]">{entry.firm || '—'}</td>
                        <td className="p-3 text-sm text-[#52525B]">{entry.notes || '—'}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="text-[#0C9168] hover:text-[#097352]"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => handleDelete(entry.entry_id)}
                              className="text-[#DC2626] hover:text-[#B91C1C]"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => handleConvertToFollowUp(entry)}
                              className="text-[#0C9168] hover:text-[#097352]"
                              title="Convert to Follow-up"
                            >
                              <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Follow-up Success Message */}
        {followUpSuccess && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 shadow-lg z-50">
            <p className="font-medium">Follow-up created successfully!</p>
          </div>
        )}
        
        {/* Follow-up Modal */}
        {showFollowUpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-[#09090B] mb-2">Convert to Trial Firm Follow-up</h2>
                <p className="text-sm text-[#71717A] mb-4">
                  Create a follow-up note for a trial firm based on this activity entry.
                </p>
                
                <form onSubmit={handleFollowUpSubmit} className="space-y-4">
                  {/* Firm Search/Select */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Select Trial Firm *
                    </label>
                    <input
                      type="text"
                      value={followUpFirmSearch}
                      onChange={(e) => setFollowUpFirmSearch(e.target.value)}
                      placeholder="Search by firm name or URL..."
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                    {followUpFirmSearch && (
                      <div className="mt-2 max-h-48 overflow-y-auto border border-[#E4E4E7] bg-white">
                        {filteredFirms.length === 0 ? (
                          <div className="p-3 text-sm text-[#71717A]">No firms found</div>
                        ) : (
                          filteredFirms.map(firm => (
                            <button
                              key={firm.site_url}
                              type="button"
                              onClick={() => {
                                setSelectedFirm(firm);
                                setFollowUpFirmSearch(`${firm.firm_name} (${firm.site_url})`);
                              }}
                              className={`w-full text-left p-3 text-sm hover:bg-[#F4F4F5] transition-colors ${
                                selectedFirm?.site_url === firm.site_url ? 'bg-[#D1FAE5]' : ''
                              }`}
                            >
                              <div className="font-medium text-[#09090B]">{firm.firm_name}</div>
                              <div className="text-xs text-[#71717A]">{firm.site_url}</div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Note */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Follow-up Note *
                    </label>
                    <textarea
                      value={followUpNote}
                      onChange={(e) => {
                        setFollowUpNote(e.target.value);
                        setFollowUpError('');
                      }}
                      required
                      rows={4}
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm resize-none"
                      placeholder="Edit or add to your note..."
                    />
                  </div>
                  
                  {followUpError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3">
                      <p className="text-sm text-red-900">{followUpError}</p>
                    </div>
                  )}

                  {/* Outcome */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Outcome *
                      </label>
                      <select
                        value={followUpOutcome}
                        onChange={(e) => setFollowUpOutcome(e.target.value)}
                        required
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      >
                        <option value="interested">Interested</option>
                        <option value="cold">Cold</option>
                        <option value="purchased">Purchased</option>
                        <option value="no_response">No Response</option>
                        <option value="extended">Extended</option>
                        <option value="deactivated">Deactivated</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Next Follow-up Date
                      </label>
                      <input
                        type="date"
                        value={followUpNextDate}
                        onChange={(e) => setFollowUpNextDate(e.target.value)}
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={savingFollowUp}
                      className="flex-1 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors disabled:opacity-50"
                    >
                      {savingFollowUp ? 'Creating...' : 'Create Follow-up'}
                    </button>
                    <button
                      type="button"
                      onClick={handleFollowUpCancel}
                      className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MyActivityPage;
